// useCategories hook - fetches the RBAC-scoped browsable category tree used by
// the search page's empty state.
import { useQuery } from '@tanstack/react-query'
import { useMe } from '../auth/hooks/useMe'
import { api } from '../services/api'
import type { Category } from '../types'

/**
 * Fetch the categories the current user is allowed to browse.
 *
 * The result is scoped and revalidated so it never keeps showing (and linking)
 * categories the user's role can no longer reach:
 *   - The cache key includes the signed-in user id, so one engineer never
 *     reuses another's allowed set from a warm cache. (getCategories is authed
 *     by token — the id is only a cache-key discriminator.)
 *   - A short staleTime plus focus/mount refetch means an access change an
 *     admin makes while the search page stays open is picked up on the next
 *     revalidation; the backend enforces the new access, so the refetch returns
 *     the updated set. (Without this, the previous 10-minute unversioned cache
 *     could keep displaying revoked categories that then return no results.)
 *   - placeholderData keeps the loaded tree on screen during a refetch, so the
 *     added revalidations don't flash the loading skeleton.
 *
 * @param enabled - Gate the query (e.g. only fetch once a session exists).
 *                  Defaults to true.
 */
export function useCategories(enabled = true) {
  const { data: meData } = useMe()
  const userId = meData?.id ?? 'anonymous'

  return useQuery<Category[]>({
    queryKey: ['browse-categories', userId],
    queryFn: () => api.getCategories(),
    staleTime: 1000 * 60 * 2, // 2 minutes — bounded RBAC staleness
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    placeholderData: (previous) => previous,
    enabled,
  })
}
